import { FilterDtoProps } from 'components/common/ComposableFilters/FiltersDto';
import {
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
} from 'date-fns';
import { compact } from 'lodash';
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useState,
} from 'react';
import { useHistory } from 'react-router';
import { SingleValue } from 'react-select/dist/declarations/src/types';
import { useQuery } from 'utils/useQuery';
import { DateRangeLabel, DateRangeOption, GroupBy, Metric } from './types';

interface StoreMetricsContextProps {
  selectedDateRange: DateRangeOption;
  dateRangeOptions: DateRangeOption[];
  selectedGroupBy: GroupBy;
  selectedMetrics: Metric[];
  setSelectedMetrics: Dispatch<SetStateAction<Metric[]>>;
  metrics: Metric[];
  handleSetSelectDateRange: (DateRangeOption: DateRangeOption) => void;
  handleSelectGroupBy: (groupByOption: SingleValue<GroupBy>) => void;
  handleSelectMetric: (metric: Metric) => void;
  handleRemoveMetric: (metric: Metric) => void;
  expandAllCards: boolean;
  handleExpandAllCards: ({ state }: { state: boolean }) => void;
  handleExpandCard: (metric: Metric) => void;
  localStorageKey: string;
  handleSaveMetricsToLocalStorage: (metrics: Metric[]) => void;
  handleLoadMetricsFromLocalStorage: () => Metric[] | undefined;
}

export const StoreMetricsContext = createContext(
  {} as StoreMetricsContextProps
);

export const StoreMetricsProvider = ({ children }: { children: ReactNode }) => {
  const query = useQuery();
  const history = useHistory();

  const localStorageKey = 'segments - storeMetrics';

  function handleSaveMetricsToLocalStorage(metrics: Metric[]) {
    localStorage.setItem(localStorageKey, JSON.stringify(metrics));
  }

  function handleLoadMetricsFromLocalStorage() {
    const storedMetrics = localStorage.getItem(localStorageKey);
    if (storedMetrics) {
      return JSON.parse(storedMetrics) as Metric[];
    }
  }

  const dateRangeOptions: DateRangeOption[] = [
    {
      label: DateRangeLabel.Last7Days,
      start_date: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
      end_date: format(new Date(), 'yyyy-MM-dd'),
      groupBy: [GroupBy.Day],
    },
    {
      label: DateRangeLabel.Last30Days,
      start_date: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
      end_date: format(new Date(), 'yyyy-MM-dd'),
      groupBy: [GroupBy.Day],
    },
    {
      label: DateRangeLabel.Last90Days,
      start_date: format(subDays(new Date(), 90), 'yyyy-MM-dd'),
      end_date: format(new Date(), 'yyyy-MM-dd'),
      groupBy: [GroupBy.Week, GroupBy.Day],
    },
    {
      label: DateRangeLabel.Last365Days,
      start_date: format(subDays(new Date(), 365), 'yyyy-MM-dd'),
      end_date: format(new Date(), 'yyyy-MM-dd'),
      groupBy: [GroupBy.Month, GroupBy.Week, GroupBy.Day],
    },
    {
      label: DateRangeLabel.LastWeek,
      start_date: format(startOfWeek(subWeeks(new Date(), 1)), 'yyyy-MM-dd'),
      end_date: format(endOfWeek(subWeeks(new Date(), 1)), 'yyyy-MM-dd'),
      groupBy: [GroupBy.Day],
    },
    {
      label: DateRangeLabel.LastMonth,
      start_date: format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'),
      end_date: format(endOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'),
      groupBy: [GroupBy.Day],
    },
    {
      label: DateRangeLabel.LastQuarter,
      start_date: format(
        startOfQuarter(subQuarters(new Date(), 1)),
        'yyyy-MM-dd'
      ),
      end_date: format(endOfQuarter(subQuarters(new Date(), 1)), 'yyyy-MM-dd'),
      groupBy: [GroupBy.Week, GroupBy.Day],
    },
    {
      label: DateRangeLabel.LastYear,
      start_date: format(startOfYear(subYears(new Date(), 1)), 'yyyy-MM-dd'),
      end_date: format(endOfYear(subYears(new Date(), 1)), 'yyyy-MM-dd'),
      groupBy: [GroupBy.Month, GroupBy.Week, GroupBy.Day],
    },
    {
      label: DateRangeLabel.YearToDate,
      start_date: format(new Date(), 'yyyy-01-01'),
      end_date: format(new Date(), 'yyyy-MM-dd'),
      groupBy: [GroupBy.Week, GroupBy.Day, GroupBy.Month],
    },
  ];

  const getSelectedDateRangeInitialState = () => {
    const startDate = query.get('start_date');
    const endDate = query.get('end_date');

    const defaultDateRange = dateRangeOptions.find(
      (option) => option.label === DateRangeLabel.Last365Days
    ) as DateRangeOption;

    if (startDate && endDate) {
      const dateRange = dateRangeOptions.find(
        (option) =>
          option.start_date === startDate && option.end_date === endDate
      );

      return dateRange ? dateRange : defaultDateRange;
    }
    return defaultDateRange;
  };
  const [selectedDateRange, setSelectedDateRange] = useState(
    getSelectedDateRangeInitialState
  );
  const handleSetSelectDateRange = (dateRangeOption: DateRangeOption) => {
    if (dateRangeOption) {
      setSelectedDateRange(dateRangeOption);

      query.set('start_date', dateRangeOption.start_date);
      query.set('end_date', dateRangeOption.end_date);

      handleSelectGroupBy(dateRangeOption.groupBy[0]);

      history.push({
        search: query.toString(),
      });
    }
  };

  const getGroupByInitialState = () => {
    const groupBy = query.get('group_by');

    if (groupBy && Object.values(GroupBy).includes(groupBy as GroupBy)) {
      return groupBy as GroupBy;
    }

    return selectedDateRange.groupBy[0];
  };

  const handleSelectGroupBy = (groupByOption: SingleValue<GroupBy>) => {
    if (groupByOption) {
      setSelectedGroupBy(groupByOption);
      query.set('group_by', groupByOption);
      history.push({
        search: query.toString(),
      });
    }
  };

  const [selectedGroupBy, setSelectedGroupBy] = useState<GroupBy>(
    getGroupByInitialState
  );

  const dashboardPreset = {
    filter: {
      filter_operator: 'and',
      filters: [
        {
          name: 'ordered_date',
          operator: 'gte',
          value: selectedDateRange.start_date,
        },
        {
          name: 'ordered_date',
          operator: 'lt',
          value: selectedDateRange.end_date,
        },
        {
          name: 'financial_status',
          operator: 'contains',
          value: ['paid', 'partially_paid', 'partially_refunded'],
        },
        {
          name: 'subtotal_status',
          operator: 'contains',
          value: ['paid'],
        },
      ],
    },
  };

  const metrics: Metric[] = [
    {
      id: 'aclv',
      label: 'Average customer lifetime value',
      format: 'currency',
      description:
        "A 2-year forward-looking predictive calculation based on your customers' purchase behavior for customers who had an order in the date range",
      learnMoreUrl:
        'https://help.tresl.co/en/articles/4324698-customer-lifetime-value-clv',
      expandedCard: false,
      defaultSelected: false,
      filters: dashboardPreset as FilterDtoProps,
    },
    {
      id: 'acr',
      label: 'Average emails clicked / opened',
      format: 'percent',
      description: '',
      learnMoreUrl: '',
      expandedCard: false,
      defaultSelected: false,
      filters: null,
    },
    {
      id: 'aov',
      label: 'Average order value',
      format: 'currency',
      description:
        'The average dollar amount spent each time a customer places an order. Please note, there is a minor difference in how we calculate AOV compared to Shopify because we base it off the subtotal price (price after discount but before shipping & taxes).',
      learnMoreUrl: '',
      expandedCard: false,
      defaultSelected: true,
      filters: null,
    },
    {
      id: 'arpu',
      label: 'Average revenue per user',
      format: 'currency',
      description:
        'Average net revenue per customer. This is derived from Total Revenue / Number of buyers',
      learnMoreUrl: '',
      expandedCard: false,
      defaultSelected: false,
      filters: null,
    },
    {
      id: 'aio',
      label: 'Average number of items per order',
      format: 'number',
      description: '',
      learnMoreUrl: '',
      expandedCard: false,
      defaultSelected: false,
      filters: null,
    },
    {
      id: 'aip',
      label: 'Average item price',
      format: 'currency',
      description:
        'Average price per item sold. This is derived from Total Revenue / Average number of items per order * Number of orders',
      learnMoreUrl: '',
      expandedCard: false,
      defaultSelected: true,
      filters: null,
    },
    {
      id: 'gross_margin',
      label: 'Gross margin',
      format: 'percent',
      description: 'How much of your net revenue you keep as gross profit.',
      learnMoreUrl: '',
      expandedCard: false,
      defaultSelected: true,
      filters: null,
    },
    {
      id: 'gross_profit',
      label: 'Gross profit',
      format: 'currency',
      description:
        'Profit from sales, after taking out discounts, refunds, and CoGS, before shipping and taxes.',
      learnMoreUrl: '',
      expandedCard: false,
      defaultSelected: false,
      filters: null,
    },
    {
      id: 'num_buyers',
      label: 'Number of buyers',
      format: 'number',
      description:
        'Number of customers in the selected period with at least 1 order',
      learnMoreUrl: 'https://help.tresl.co/en/articles/4317280-customers',
      expandedCard: false,
      defaultSelected: false,
      filters: null,
    },
    {
      id: 'num_churned',
      label: 'Number of customers churned',
      format: 'number',
      description: '',
      learnMoreUrl: '',
      expandedCard: false,
      defaultSelected: false,
      filters: null,
    },
    {
      id: 'num_orders',
      label: 'Number of orders',
      format: 'number',
      description: '',
      learnMoreUrl: '',
      expandedCard: false,
      defaultSelected: true,
      filters: null,
    },
    // {
    //   id: 'num_users',
    //   label: 'Number of users',
    //   format: 'number',
    //   description: '',
    //   learnMoreUrl: '',
    //   expandedCard: false,
    //   defaultSelected: true,
    //   filters: dashboardPreset as FilterDtoProps,
    // },
    {
      id: 'total_revenue',
      label: 'Net revenue',
      format: 'currency',
      description:
        'Total sales in the selected period after discounts and refunds, but before shipping and taxes.',
      learnMoreUrl: 'https://help.tresl.co/en/articles/4317164-net-revenue',
      expandedCard: false,
      defaultSelected: true,
      filters: null,
    },
  ];

  const getSelectedMetricsInitialState = () => {
    let initialState: Metric[] = [];

    const parsedMetrics = query.has('metrics')
      ? (query.get('metrics') as string)?.split(',')
      : [];

    if (parsedMetrics.length > 0) {
      const metricsToAdd = compact(
        parsedMetrics.map((metricId) => {
          const metric = metrics.find((m) => m.id === metricId);
          return metric ? metric : undefined;
        })
      );

      initialState = metricsToAdd;
    } else {
      const storedMetrics = handleLoadMetricsFromLocalStorage();

      if (storedMetrics && storedMetrics.length > 0) {
        initialState.push(...storedMetrics);
      }
    }

    return initialState.length > 0
      ? initialState
      : metrics.filter((m) => m.defaultSelected);
  };

  const [selectedMetrics, setSelectedMetrics] = useState<Metric[]>(
    getSelectedMetricsInitialState
  );

  const handleSelectMetric = (metric: Metric) => {
    if (metric) {
      setSelectedMetrics([...selectedMetrics, metric]);
      query.set(
        'metrics',
        [...selectedMetrics, metric].map((item) => item.id).join(',')
      );
      history.push({
        search: query.toString(),
      });
    }
  };

  const handleRemoveMetric = (metric: Metric) => {
    const newSelectedMetrics = selectedMetrics.filter(
      (item) => item.id !== metric.id
    );

    setSelectedMetrics(newSelectedMetrics);
    query.set('metrics', newSelectedMetrics.map((item) => item.id).join(','));
    history.push({
      search: query.toString(),
    });

    handleSaveMetricsToLocalStorage(newSelectedMetrics);
  };

  const [expandAllCards, setExpandAllCards] = useState(() => {
    const expand_all_cards = query.get('expand_all_cards');
    return Boolean(expand_all_cards);
  });

  const handleExpandAllCards = ({ state }: { state: boolean }) => {
    setExpandAllCards(state);

    if (state === false) {
      query.delete('expand_all_cards');

      setSelectedMetrics((prev) =>
        prev.map((item) => ({ ...item, expandedCard: false }))
      );
    } else {
      query.set('expand_all_cards', 'true');
      setSelectedMetrics((prev) =>
        prev.map((item) => ({ ...item, expandedCard: true }))
      );
    }

    history.push({
      search: query.toString(),
    });
  };

  const handleExpandCard = (metric: Metric) => {
    const newSelectedMetrics = [...selectedMetrics];
    const index = newSelectedMetrics.findIndex((m) => m.id === metric.id);
    newSelectedMetrics[index] = {
      ...newSelectedMetrics[index],
      expandedCard: !newSelectedMetrics[index].expandedCard,
    };

    setSelectedMetrics(newSelectedMetrics);

    handleSaveMetricsToLocalStorage(newSelectedMetrics);

    if (expandAllCards) {
      setExpandAllCards(false);
      query.delete('expand_all_cards');
      history.push({
        search: query.toString(),
      });
    }
  };

  return (
    <StoreMetricsContext.Provider
      value={{
        selectedDateRange,
        dateRangeOptions,
        selectedGroupBy,
        selectedMetrics,
        setSelectedMetrics,
        metrics,
        handleSetSelectDateRange,
        handleSelectGroupBy,
        handleSelectMetric,
        handleRemoveMetric,
        expandAllCards,
        handleExpandAllCards,
        handleExpandCard,
        handleSaveMetricsToLocalStorage,
        localStorageKey,
        handleLoadMetricsFromLocalStorage,
      }}
    >
      {children}
    </StoreMetricsContext.Provider>
  );
};
